import { MailCheck } from 'lucide-react';

import { OtpCells } from '@/components/patterns/OtpCells';

import { Field } from '../PreviewFields';
import { PreviewHeader } from '../PreviewHeader';

/**
 * Email Verification — the applicant's two screens, previewed in the builder.
 *
 * TWO SCREENS, like Face Match and Liveness: you cannot ask for a code and show the code boxes at
 * once, because the code does not exist until the address is submitted. The journey therefore gains
 * a screen while the step count stays at one — see `screensFor` in MobilePreview.
 *
 * NO SIGNCARE API IS BEHIND THIS STEP. We send the mail ourselves, so unlike every other preview
 * screen in this folder there is no provider whose form we are mirroring — the screens are the
 * product, and the applicant runner (`steps/email/`) renders the same two stages for real.
 *
 * OUR OWN DESIGN, following the rhythm of the surrounding screens (PreviewHeader + Field, the
 * boxed code row from `patterns/OtpCells`) rather than inventing a look for one step.
 */

export interface EmailVerificationScreenProps {
  /** Live form value — passed by the session runtime; omitted in the builder mock. */
  value?: string;
  onChange?: (value: string) => void;
  /** Validation message (session runtime) — shown under the field. */
  error?: string;
}

/** Stage 1 — the applicant types the address they want verified. */
export function EmailVerificationScreen({ value, onChange, error }: EmailVerificationScreenProps) {
  return (
    <>
      <PreviewHeader title="Email Verification" subtitle="Enter your email address to continue" />
      <Field
        label="Email Address"
        placeholder="you@example.com"
        // `email` rather than `text`: it is the one keyboard hint that puts "@" and "." on the
        // primary layer, and this field accepts nothing a text keyboard offers beyond that.
        inputMode="email"
        value={value}
        onChange={onChange}
        error={error}
      />
      <p className="mt-2 text-[14px] leading-tight text-[#666666]">
        We'll email you a 6-digit code. Entering it confirms the address is yours.
      </p>
    </>
  );
}

export interface EmailOtpScreenProps {
  /** The address the code went to, masked. The builder mock shows a placeholder. */
  maskedEmail?: string;
  code?: string;
  onCodeChange?: (next: string) => void;
  onComplete?: (fullCode: string) => void;
  error?: string;
  disabled?: boolean;
}

/** Stage 2 — the applicant types the code back. */
export function EmailOtpScreen({
  maskedEmail,
  code,
  onCodeChange,
  onComplete,
  error,
  disabled,
}: EmailOtpScreenProps) {
  return (
    <>
      <PreviewHeader
        title="Enter the code"
        // Masked, exactly as the mobile gate does it: enough for the applicant to confirm they
        // typed the right address, without printing it back in full on a screen someone may be
        // looking over.
        subtitle={`We sent a 6-digit code to ${maskedEmail ?? 'y***u@example.com'}`}
      />

      {/* The envelope sits above the boxes so the screen reads as "check your mail", which is the
          one instruction that matters here. */}
      <div className="mb-5 flex justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF3FF] text-primary">
          <MailCheck size={26} strokeWidth={1.8} />
        </span>
      </div>

      <OtpCells
        value={code ?? ''}
        // Inert in the builder mock (no handlers passed) but still rendered, so an admin sees the
        // real control rather than a picture of one.
        onChange={onCodeChange ?? (() => {})}
        onComplete={onComplete ?? (() => {})}
        disabled={disabled}
      />

      {error && <p className="mt-3 text-center text-[13px] text-destructive">{error}</p>}
    </>
  );
}
